import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Plus, Loader2, Sparkles, ChevronDown, ChevronRight, Trash2, FileText,
  Copy, Download, Mail, ArrowLeft, CheckCircle2, Wand2, Globe, ExternalLink, Link as LinkIcon,
  Pencil, Save, X, AlertTriangle, Bot, Send, FileDown,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ax = axios.create({ withCredentials: true });

const FLOW_LABEL = {
  deep_dive: 'Full Deep Dive',
  condensed: 'Shortened Deep Dive',
  jumpstart_20: 'Free 20-Min Jumpstart',
};

/* ---------- minimal markdown renderer (headers, bold, lists, paragraphs) ---------- */
function renderInline(text) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) =>
    p.startsWith('**') && p.endsWith('**')
      ? <strong key={i}>{p.slice(2, -2)}</strong>
      : <React.Fragment key={i}>{p}</React.Fragment>
  );
}

export function Markdown({ text }) {
  const lines = (text || '').split('\n');
  const blocks = [];
  let list = [];
  const flush = () => {
    if (list.length) {
      blocks.push(<ul key={`ul-${blocks.length}`} className="list-disc pl-5 space-y-1 my-2">{list}</ul>);
      list = [];
    }
  };
  lines.forEach((raw, idx) => {
    const line = raw.replace(/\s+$/, '');
    if (/^#\s+/.test(line)) { flush(); blocks.push(<h1 key={idx} className="text-2xl font-bold mt-5 mb-2" style={{ color: 'var(--jp-h1,#0A0F1C)' }}>{renderInline(line.replace(/^#\s+/, ''))}</h1>); }
    else if (/^##\s+/.test(line)) { flush(); blocks.push(<h2 key={idx} className="text-xl font-bold mt-5 mb-2 text-[#1D4ED8]">{renderInline(line.replace(/^##\s+/, ''))}</h2>); }
    else if (/^###\s+/.test(line)) { flush(); blocks.push(<h3 key={idx} className="text-base font-bold mt-4 mb-1">{renderInline(line.replace(/^###\s+/, ''))}</h3>); }
    else if (/^[-*]\s+/.test(line)) { list.push(<li key={idx} className="text-sm leading-relaxed">{renderInline(line.replace(/^[-*]\s+/, ''))}</li>); }
    else if (line.trim() === '') { flush(); }
    else { flush(); blocks.push(<p key={idx} className="text-sm leading-relaxed my-2">{renderInline(line)}</p>); }
  });
  flush();
  return <div data-testid="blueprint-markdown">{blocks}</div>;
}

/* ---------- new session modal ---------- */
function NewSessionModal({ onClose, onCreated, context = 'business', initial = {} }) {
  const [flow, setFlow] = useState('deep_dive');
  const [clientName, setClientName] = useState(initial.client_name || '');
  const [clientEmail, setClientEmail] = useState(initial.client_email || '');
  const [businessName, setBusinessName] = useState(initial.business_name || '');
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    ax.get(`${API}/coaching/clients`).then((r) => setClients(r.data || [])).catch(() => {});
  }, []);

  const pickClient = (id) => {
    setClientId(id);
    const c = clients.find((x) => x.client_id === id);
    if (c) { setClientName(c.name || ''); setClientEmail(c.email || ''); }
  };

  const submit = async () => {
    if (!clientName.trim()) { toast.error('Client name is required'); return; }
    setSaving(true);
    try {
      const { data } = await ax.post(`${API}/coaching/sessions`, {
        flow, client_name: clientName, client_email: clientEmail, business_name: businessName, client_id: clientId || null, context,
      });
      onCreated(data);
    } catch {
      toast.error('Could not create session');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="new-session-modal" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-lg p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-xl font-bold text-[#0A0F1C] mb-4">{context === 'nonprofit' ? 'New Seven Stage Nonprofit Jumpstart Session' : 'New Jumpstart Session'}</h3>
        <div className="space-y-4">
          <div>
            <label className="text-xs font-bold uppercase tracking-wide text-[#64748B]">Session type</label>
            <div className="grid grid-cols-3 gap-2 mt-1.5">
              {['deep_dive', 'condensed', 'jumpstart_20'].map((f) => (
                <button key={f} onClick={() => setFlow(f)} data-testid={`flow-${f}`}
                  className={`px-2 py-2.5 rounded-lg border text-xs font-semibold transition-all ${flow === f ? 'border-[#1D4ED8] bg-[#EFF6FF] text-[#1D4ED8]' : 'border-[#E2E8F0] text-[#475569] hover:border-[#1D4ED8]'}`}>
                  {FLOW_LABEL[f]}
                </button>
              ))}
            </div>
            <p className="text-xs text-[#94A3B8] mt-1.5">
              {flow === 'deep_dive' && 'The complete framework — every question. Best for the full $197 session.'}
              {flow === 'condensed' && '~30-min curated interview — a strong general base. Recommend the Deep Dive for the full picture.'}
              {flow === 'jumpstart_20' && 'Quick set for the free 20-minute Jumpstart Session.'}
            </p>
          </div>
          {clients.length > 0 && (
            <div>
              <label className="text-xs font-bold uppercase tracking-wide text-[#64748B]">Link existing client (optional)</label>
              <select value={clientId} onChange={(e) => pickClient(e.target.value)} data-testid="link-client-select"
                className="w-full mt-1.5 px-3 py-2.5 rounded-lg border border-[#E2E8F0] text-sm">
                <option value="">— New / unlinked —</option>
                {clients.map((c) => <option key={c.client_id} value={c.client_id}>{c.name} ({c.email})</option>)}
              </select>
            </div>
          )}
          <div>
            <label className="text-xs font-bold uppercase tracking-wide text-[#64748B]">Client name *</label>
            <input value={clientName} onChange={(e) => setClientName(e.target.value)} data-testid="client-name-input"
              className="w-full mt-1.5 px-3 py-2.5 rounded-lg border border-[#E2E8F0] text-sm" placeholder="e.g. Jane Founder" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-bold uppercase tracking-wide text-[#64748B]">Client email</label>
              <input value={clientEmail} onChange={(e) => setClientEmail(e.target.value)} data-testid="client-email-input"
                className="w-full mt-1.5 px-3 py-2.5 rounded-lg border border-[#E2E8F0] text-sm" placeholder="jane@email.com" />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wide text-[#64748B]">Business name</label>
              <input value={businessName} onChange={(e) => setBusinessName(e.target.value)} data-testid="business-name-input"
                className="w-full mt-1.5 px-3 py-2.5 rounded-lg border border-[#E2E8F0] text-sm" placeholder="BrightPath" />
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm font-semibold text-[#475569] hover:bg-[#F1F5F9]">Cancel</button>
          <button onClick={submit} disabled={saving} data-testid="create-session-btn"
            className="px-5 py-2 rounded-lg text-sm font-semibold bg-[#1D4ED8] text-white hover:bg-[#2563EB] disabled:opacity-60 inline-flex items-center gap-2">
            {saving ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />} Start session
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---------- interview (stage chapters -> phases -> questions) ---------- */
export function Interview({ session, framework, onSaveAnswer }) {
  const [open, setOpen] = useState({});
  const answers = session.answers || {};
  const toggle = (k) => setOpen((o) => ({ ...o, [k]: !o[k] }));

  // Auto-open the first stage once the framework has loaded (without overriding
  // the user's own expand/collapse choices once they've interacted).
  useEffect(() => {
    if (framework[0]) setOpen((o) => (Object.keys(o).length ? o : { [framework[0].key]: true }));
  }, [framework]);

  return (
    <div className="space-y-3" data-testid="interview">
      {framework.map((stage) => {
        const stageQs = stage.phases.flatMap((p) => p.questions);
        const answered = stageQs.filter((q) => (answers[q.id] || '').trim()).length;
        const isOpen = !!open[stage.key];
        return (
          <div key={stage.key} className="border border-[#E2E8F0] rounded-xl overflow-hidden bg-white" data-testid={`stage-${stage.key}`}>
            <button onClick={() => toggle(stage.key)} data-testid={`stage-toggle-${stage.key}`}
              className="w-full flex items-center justify-between px-4 py-3.5 text-left hover:bg-[#F8FAFC]">
              <div>
                <div className="font-bold text-[#0A0F1C]">{stage.title}</div>
                <div className="text-xs text-[#64748B] mt-0.5">{stage.subtitle}</div>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <span className={`text-xs font-bold px-2 py-1 rounded-full ${answered === stageQs.length && stageQs.length ? 'bg-emerald-100 text-emerald-700' : 'bg-[#F1F5F9] text-[#64748B]'}`}>
                  {answered}/{stageQs.length}
                </span>
                {isOpen ? <ChevronDown size={18} className="text-[#94A3B8]" /> : <ChevronRight size={18} className="text-[#94A3B8]" />}
              </div>
            </button>
            {isOpen && (
              <div className="px-4 pb-4 space-y-5 border-t border-[#F1F5F9] pt-4">
                {stage.phases.map((phase) => (
                  <div key={phase.id} data-testid={`phase-${phase.id}`}>
                    <div className="text-sm font-bold text-[#1D4ED8]">{phase.title}</div>
                    <div className="text-xs text-[#94A3B8] mb-2">{phase.goal}</div>
                    <div className="space-y-3">
                      {phase.questions.map((q) => (
                        <div key={q.id}>
                          <label className="text-sm text-[#334155] block mb-0.5">{q.text}</label>
                          {q.example && (
                            <p className="text-xs text-[#94A3B8] italic mb-1.5" data-testid={`example-${q.id}`}>{q.example}</p>
                          )}
                          <textarea
                            data-testid={`answer-${q.id}`}
                            defaultValue={answers[q.id] || ''}
                            onBlur={(e) => onSaveAnswer(q.id, e.target.value)}
                            rows={2}
                            className="w-full px-3 py-2 rounded-lg border border-[#E2E8F0] text-sm focus:border-[#1D4ED8] focus:ring-1 focus:ring-[#1D4ED8] outline-none resize-y"
                            placeholder="Capture the client's answer…"
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

/* ---------- assign blueprint to a client's member portal ---------- */
function AssignPanel({ session, onAssigned }) {
  const [open, setOpen] = useState(false);
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState(session.client_id || '');
  const [email, setEmail] = useState(session.client_email || '');
  const [notify, setNotify] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    ax.get(`${API}/coaching/clients`).then((r) => setClients(r.data || [])).catch(() => {});
  }, []);

  const pick = (id) => {
    setClientId(id);
    const c = clients.find((x) => x.client_id === id);
    if (c) setEmail(c.email || '');
  };

  const assign = async () => {
    const em = email.trim().toLowerCase();
    if (!clientId && (!em || !em.includes('@'))) { toast.error('Pick a client or enter a valid email'); return; }
    setSaving(true);
    try {
      const { data } = await ax.post(`${API}/coaching/sessions/${session.session_id}/assign-client`, {
        client_id: clientId || null, client_email: em || null, notify,
      });
      toast.success(data.emailed ? `Sent to ${data.client_email} — they've been emailed a link` : `Assigned to ${data.client_email}`);
      onAssigned(data);
      setOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not assign blueprint');
    } finally { setSaving(false); }
  };

  const assigned = session.assigned_at && session.client_email;

  return (
    <div className="rounded-xl border border-[#E2E8F0] bg-[#F8FAFC] mb-4 overflow-hidden" data-testid="assign-panel">
      <button onClick={() => setOpen((o) => !o)} data-testid="assign-toggle"
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-[#F1F5F9]">
        <div className="flex items-center gap-2 flex-wrap">
          <Send size={16} className="text-[#1D4ED8]" />
          <span className="font-bold text-sm text-[#0A0F1C]">Send to client's member portal</span>
          {assigned && (
            <span className="inline-flex items-center gap-1 text-[10px] font-bold text-emerald-600" data-testid="assign-status">
              <CheckCircle2 size={12} /> Sent to {session.client_email}
            </span>
          )}
        </div>
        {open ? <ChevronDown size={18} className="text-[#94A3B8]" /> : <ChevronRight size={18} className="text-[#94A3B8]" />}
      </button>
      {open && (
        <div className="px-4 pb-4">
          <p className="text-xs text-[#64748B] mb-3">
            Assign this blueprint to a client so it appears in their portal under <strong>My Blueprint</strong>.
            They'll be emailed a link automatically. If they create their account later with this email, it unlocks for them.
          </p>
          {clients.length > 0 && (
            <div className="mb-2">
              <label className="text-xs font-bold uppercase tracking-wide text-[#64748B]">Existing client (optional)</label>
              <select value={clientId} onChange={(e) => pick(e.target.value)} data-testid="assign-client-select"
                className="w-full mt-1.5 px-3 py-2.5 rounded-lg border border-[#E2E8F0] text-sm">
                <option value="">— Enter email below —</option>
                {clients.map((c) => <option key={c.client_id} value={c.client_id}>{c.name} ({c.email})</option>)}
              </select>
            </div>
          )}
          <label className="text-xs font-bold uppercase tracking-wide text-[#64748B]">Client email</label>
          <input value={email} onChange={(e) => { setEmail(e.target.value); setClientId(''); }} data-testid="assign-email-input"
            placeholder="client@email.com" className="w-full mt-1.5 px-3 py-2.5 rounded-lg border border-[#E2E8F0] text-sm" />
          <label className="flex items-center gap-2 mt-3 text-xs text-[#475569] cursor-pointer">
            <input type="checkbox" checked={notify} onChange={(e) => setNotify(e.target.checked)} data-testid="assign-notify-checkbox" />
            Email the client a notification with a portal link
          </label>
          <button onClick={assign} disabled={saving} data-testid="assign-submit-btn"
            className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#1D4ED8] text-white text-sm font-bold hover:bg-[#2563EB] disabled:opacity-60">
            {saving ? <><Loader2 size={15} className="animate-spin" /> Sending…</> : <><Send size={15} /> Send to portal</>}
          </button>
        </div>
      )}
    </div>
  );
}

/* ---------- deliverable viewer ---------- */
function Blueprint({ session, onEmail, onBuildWebsite, building, onSaveDeliverable, onRefine, refining, onAssigned }) {
  const md = session.deliverable?.markdown || '';
  const [emailing, setEmailing] = useState(false);
  const [emailTo, setEmailTo] = useState(session.client_email || '');
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(md);
  const [saving, setSaving] = useState(false);
  const [instruction, setInstruction] = useState('');
  const siteUrl = session.website_status === 'ready' && session.website_slug
    ? `${process.env.REACT_APP_BACKEND_URL}/api/coaching/site/${session.website_slug}`
    : null;

  const copy = () => { navigator.clipboard.writeText(md); toast.success('Blueprint copied'); };
  const copyLink = () => { navigator.clipboard.writeText(siteUrl); toast.success('Website link copied'); };
  const download = () => {
    const blob = new Blob([md], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `${(session.business_name || session.client_name || 'blueprint').replace(/\s+/g, '-')}-jumpstart-blueprint.md`;
    a.click(); URL.revokeObjectURL(url);
  };
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const downloadPdf = async () => {
    setDownloadingPdf(true);
    try {
      const r = await ax.get(`${API}/coaching/sessions/${session.session_id}/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(session.business_name || session.client_name || 'blueprint').replace(/\s+/g, '-')}-Blueprint.pdf`;
      a.click(); URL.revokeObjectURL(url);
      toast.success('Branded PDF downloaded');
    } catch { toast.error('Could not generate PDF'); }
    finally { setDownloadingPdf(false); }
  };
  const sendEmail = async () => {
    if (!emailTo.trim()) { toast.error('Enter an email'); return; }
    setEmailing(true);
    try { await onEmail(emailTo); toast.success(`Blueprint sent to ${emailTo}`); }
    catch { toast.error('Could not send email'); }
    finally { setEmailing(false); }
  };
  const startEdit = () => { setDraft(md); setEditing(true); };
  const saveEdit = async () => {
    setSaving(true);
    try { await onSaveDeliverable(draft); setEditing(false); toast.success('Blueprint saved'); }
    catch { toast.error('Could not save'); }
    finally { setSaving(false); }
  };
  const askAi = async () => {
    if (instruction.trim().length < 3) { toast.error('Tell the AI what to change'); return; }
    await onRefine(instruction.trim());
    setInstruction('');
  };

  return (
    <div data-testid="blueprint-view">
      <div className="flex flex-wrap items-center gap-2 mb-4">
        {!editing ? (
          <button onClick={startEdit} data-testid="edit-blueprint-btn" className="inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-2 rounded-lg border border-[#1D4ED8] text-[#1D4ED8] hover:bg-[#EFF6FF]"><Pencil size={15} /> Edit</button>
        ) : (
          <>
            <button onClick={saveEdit} disabled={saving} data-testid="save-blueprint-btn" className="inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-2 rounded-lg bg-[#1D4ED8] text-white hover:bg-[#2563EB] disabled:opacity-60">{saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Save</button>
            <button onClick={() => setEditing(false)} data-testid="cancel-edit-btn" className="inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-2 rounded-lg border border-[#E2E8F0] text-[#475569] hover:bg-[#F1F5F9]"><X size={15} /> Cancel</button>
          </>
        )}
        <button onClick={copy} data-testid="copy-blueprint-btn" className="inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-2 rounded-lg border border-[#E2E8F0] text-[#475569] hover:border-[#1D4ED8] hover:text-[#1D4ED8]"><Copy size={15} /> Copy</button>
        <button onClick={download} data-testid="download-blueprint-btn" className="inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-2 rounded-lg border border-[#E2E8F0] text-[#475569] hover:border-[#1D4ED8] hover:text-[#1D4ED8]"><Download size={15} /> Download</button>
        <button onClick={downloadPdf} disabled={downloadingPdf} data-testid="print-blueprint-btn" className="inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-2 rounded-lg border border-[#E2E8F0] text-[#475569] hover:border-[#1D4ED8] hover:text-[#1D4ED8] disabled:opacity-60">{downloadingPdf ? <Loader2 size={15} className="animate-spin" /> : <FileDown size={15} />} {downloadingPdf ? 'Building PDF…' : 'Branded PDF'}</button>
        <div className="flex items-center gap-1.5 ml-auto">
          <input value={emailTo} onChange={(e) => setEmailTo(e.target.value)} placeholder="client@email.com" data-testid="email-to-input"
            className="px-3 py-2 rounded-lg border border-[#E2E8F0] text-sm w-48" />
          <button onClick={sendEmail} disabled={emailing} data-testid="email-blueprint-btn"
            className="inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-2 rounded-lg bg-[#F97316] text-white hover:bg-[#EA580C] disabled:opacity-60">
            {emailing ? <Loader2 size={15} className="animate-spin" /> : <Mail size={15} />} Email
          </button>
        </div>
      </div>

      {/* Assign to client's member portal */}
      <AssignPanel session={session} onAssigned={onAssigned} />

      {/* Ask AI to edit */}
      <div className="flex items-center gap-2 mb-4 rounded-xl border border-[#E2E8F0] bg-[#F8FAFC] p-2.5" data-testid="ai-edit-bar">
        <Bot size={18} className="text-[#1D4ED8] shrink-0 ml-1" />
        <input value={instruction} onChange={(e) => setInstruction(e.target.value)} disabled={refining}
          onKeyDown={(e) => e.key === 'Enter' && !refining && askAi()} data-testid="ai-edit-input"
          placeholder="Ask AI to edit — e.g. 'Fix the spelling of the client's name to Natasha Greene'"
          className="flex-1 px-3 py-2 rounded-lg border border-[#E2E8F0] text-sm outline-none focus:border-[#1D4ED8]" />
        <button onClick={askAi} disabled={refining} data-testid="ai-edit-submit"
          className="inline-flex items-center gap-1.5 text-sm font-bold px-4 py-2 rounded-lg bg-[#1D4ED8] text-white hover:bg-[#2563EB] disabled:opacity-60 shrink-0">
          {refining ? <><Loader2 size={15} className="animate-spin" /> Editing…</> : <><Wand2 size={15} /> Ask AI</>}
        </button>
      </div>

      {/* One-click website builder */}
      <div className="bg-gradient-to-r from-[#0F172A] to-[#1E293B] rounded-xl p-5 mb-4 text-white" data-testid="website-panel">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-[#FB923C]">
              <Globe size={14} /> Turn this blueprint into a website
            </div>
            <p className="text-sm text-[#CBD5E1] mt-1 max-w-xl">One click builds a launch-ready single-page site from this blueprint — share the live link with your client on the spot.</p>
          </div>
          {siteUrl ? (
            <div className="flex flex-wrap items-center gap-2">
              <a href={siteUrl} target="_blank" rel="noopener noreferrer" data-testid="open-website-btn"
                className="inline-flex items-center gap-1.5 text-sm font-bold px-4 py-2 rounded-lg bg-[#F97316] hover:bg-[#EA580C] text-white">
                <ExternalLink size={15} /> Open live website
              </a>
              <button onClick={copyLink} data-testid="copy-website-link-btn" className="inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-white"><LinkIcon size={15} /> Copy link</button>
              <button onClick={onBuildWebsite} disabled={building} data-testid="rebuild-website-btn" className="inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-white disabled:opacity-60">
                {building ? <Loader2 size={15} className="animate-spin" /> : <Wand2 size={15} />} Rebuild
              </button>
            </div>
          ) : (
            <button onClick={onBuildWebsite} disabled={building} data-testid="build-website-btn"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[#F97316] hover:bg-[#EA580C] text-white font-bold disabled:opacity-70 shrink-0">
              {building ? <><Loader2 size={16} className="animate-spin" /> Building website… (~1–2 min)</> : <><Globe size={16} /> Build Website</>}
            </button>
          )}
        </div>
        {siteUrl && (
          <div className="mt-4 rounded-lg overflow-hidden border border-white/10 bg-white" data-testid="website-preview-frame">
            <iframe title="Website preview" src={siteUrl} className="w-full" style={{ height: 460, border: 0 }} sandbox="allow-scripts allow-popups" />
          </div>
        )}
      </div>
      {editing ? (
        <textarea value={draft} onChange={(e) => setDraft(e.target.value)} data-testid="blueprint-edit-textarea"
          className="w-full bg-white border border-[#1D4ED8] rounded-xl p-6 text-sm font-mono leading-relaxed outline-none resize-y" style={{ minHeight: 500 }} />
      ) : (
        <div className="bg-white border border-[#E2E8F0] rounded-xl p-6">
          <Markdown text={md} />
        </div>
      )}
    </div>
  );
}

/* ---------- auto-fill from notes or a recording ---------- */
function ImportPanel({ session, onImported, onAiError }) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState('notes');
  const [notes, setNotes] = useState('');
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [phase, setPhase] = useState(''); // uploading | transcribing | processing
  const [uploadPct, setUploadPct] = useState(0);
  const [visions, setVisions] = useState([]);
  const [visionId, setVisionId] = useState('');
  const pollRef = useRef(null);
  const sid = session.session_id;

  useEffect(() => {
    if (mode !== 'vision' || visions.length) return;
    ax.get(`${API}/admin/website-vision?person=all`).then((r) => {
      const rows = r.data || [];
      setVisions(rows);
      const mine = rows.find((v) => session.client_email && v.client_email === session.client_email);
      setVisionId((mine || rows[0] || {}).vision_id || '');
    }).catch(() => {});
  }, [mode, visions.length, session.client_email]);

  const submitVision = async () => {
    if (!visionId) { toast.error('Pick a Website Vision first'); return; }
    setBusy(true); setPhase('processing');
    try {
      await ax.post(`${API}/coaching/sessions/${sid}/import-website-vision`, { vision_id: visionId });
      startPoll();
    } catch (e) { setBusy(false); setPhase(''); toast.error(e?.response?.data?.detail || 'Could not start auto-fill'); }
  };

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const startPoll = () => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await ax.get(`${API}/coaching/sessions/${sid}`);
        if (data.import_status === 'done') {
          clearInterval(pollRef.current); pollRef.current = null;
          setBusy(false); setPhase(''); setNotes(''); setFile(null); setUploadPct(0);
          toast.success(`Auto-filled ${data.import_applied || 0} answer${data.import_applied === 1 ? '' : 's'}`);
          onImported(data);
        } else if (data.import_status === 'failed') {
          clearInterval(pollRef.current); pollRef.current = null;
          setBusy(false); setPhase('');
          onAiError(data.import_error || 'Auto-fill failed');
        } else if (data.import_status === 'processing') {
          setPhase('processing');
        }
      } catch { /* keep polling */ }
    }, 4000);
  };

  const submitNotes = async () => {
    if (notes.trim().length < 30) { toast.error('Paste at least a few sentences'); return; }
    setBusy(true); setPhase('processing');
    try {
      await ax.post(`${API}/coaching/sessions/${sid}/import-text`, { text: notes });
      startPoll();
    } catch (e) { setBusy(false); setPhase(''); toast.error(e?.response?.data?.detail || 'Could not start auto-fill'); }
  };

  const submitRecording = async () => {
    if (!file) { toast.error('Choose a recording first'); return; }
    setBusy(true); setPhase('uploading'); setUploadPct(0);
    try {
      const CHUNK = 5 * 1024 * 1024;
      const total = Math.ceil(file.size / CHUNK);
      for (let i = 0; i < total; i++) {
        const fd = new FormData();
        fd.append('chunk', file.slice(i * CHUNK, (i + 1) * CHUNK));
        fd.append('chunk_index', i);
        fd.append('total_chunks', total);
        await ax.post(`${API}/coaching/sessions/${sid}/audio-chunk`, fd);
        setUploadPct(Math.round(((i + 1) / total) * 100));
      }
      setPhase('transcribing');
      await ax.post(`${API}/coaching/sessions/${sid}/transcribe`);
      startPoll();
    } catch (e) { setBusy(false); setPhase(''); toast.error(e?.response?.data?.detail || 'Upload failed'); }
  };

  const phaseLabel = phase === 'uploading' ? `Uploading… ${uploadPct}%`
    : phase === 'transcribing' ? 'Transcribing recording… (~1–2 min)'
    : phase === 'processing' ? 'Reading & filling answers…' : '';

  return (
    <div className="border border-[#E2E8F0] rounded-xl bg-[#F8FAFC] mb-4 overflow-hidden" data-testid="import-panel">
      <button onClick={() => setOpen((o) => !o)} data-testid="import-toggle"
        className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-[#F1F5F9]">
        <div className="flex items-center gap-2">
          <Wand2 size={16} className="text-[#F97316]" />
          <span className="font-bold text-sm text-[#0A0F1C]">Auto-fill from notes or a recording</span>
        </div>
        {open ? <ChevronDown size={18} className="text-[#94A3B8]" /> : <ChevronRight size={18} className="text-[#94A3B8]" />}
      </button>
      {open && (
        <div className="px-4 pb-4">
          <p className="text-xs text-[#64748B] mb-3">Already had the conversation? Paste your notes/transcript or upload the call recording — AI will pre-populate the answers below for you to review and edit.</p>
          <div className="inline-flex rounded-lg border border-[#E2E8F0] overflow-hidden mb-3">
            <button onClick={() => setMode('notes')} data-testid="import-mode-notes" className={`px-3 py-1.5 text-sm font-semibold ${mode === 'notes' ? 'bg-[#1D4ED8] text-white' : 'text-[#475569]'}`}>Paste notes</button>
            <button onClick={() => setMode('recording')} data-testid="import-mode-recording" className={`px-3 py-1.5 text-sm font-semibold ${mode === 'recording' ? 'bg-[#1D4ED8] text-white' : 'text-[#475569]'}`}>Upload recording</button>
            <button onClick={() => setMode('vision')} data-testid="import-mode-vision" className={`px-3 py-1.5 text-sm font-semibold ${mode === 'vision' ? 'bg-[#1D4ED8] text-white' : 'text-[#475569]'}`}>From Website Vision</button>
          </div>
          {mode === 'vision' ? (
            <>
              <p className="text-xs text-[#64748B] mb-2">Already built their Website Vision? We'll use its services, programs, audience, contact info and vision to pre-fill these answers.</p>
              <select value={visionId} onChange={(e) => setVisionId(e.target.value)} disabled={busy} data-testid="import-vision-select"
                className="w-full px-3 py-2 rounded-lg border border-[#E2E8F0] text-sm bg-white">
                {visions.length === 0 && <option value="">No Website Visions yet</option>}
                {visions.map((v) => <option key={v.vision_id} value={v.vision_id}>{v.title || 'Untitled Vision'}{v.client_email ? ` (${v.client_email})` : ''}</option>)}
              </select>
              <button onClick={submitVision} disabled={busy || !visionId} data-testid="import-vision-submit"
                className="mt-2 inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#F97316] text-white text-sm font-bold hover:bg-[#EA580C] disabled:opacity-60">
                {busy ? <><Loader2 size={15} className="animate-spin" /> {phaseLabel}</> : <><Wand2 size={15} /> Auto-fill from Website Vision</>}
              </button>
            </>
          ) : mode === 'notes' ? (
            <>
              <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={5} disabled={busy} data-testid="import-notes-input"
                className="w-full px-3 py-2 rounded-lg border border-[#E2E8F0] text-sm resize-y" placeholder="Paste your call notes or transcript here…" />
              <button onClick={submitNotes} disabled={busy} data-testid="import-notes-submit"
                className="mt-2 inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#F97316] text-white text-sm font-bold hover:bg-[#EA580C] disabled:opacity-60">
                {busy ? <><Loader2 size={15} className="animate-spin" /> {phaseLabel}</> : <><Wand2 size={15} /> Auto-fill answers</>}
              </button>
            </>
          ) : (
            <>
              <input type="file" accept="audio/*,.mp3,.m4a,.wav,.mp4,.webm" disabled={busy} data-testid="import-audio-input"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                className="block w-full text-sm text-[#475569] file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-[#1D4ED8] file:text-white hover:file:bg-[#2563EB]" />
              <p className="text-xs text-[#94A3B8] mt-1">Up to ~60 minutes. mp3, m4a, wav, mp4, webm.</p>
              <button onClick={submitRecording} disabled={busy || !file} data-testid="import-audio-submit"
                className="mt-2 inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#F97316] text-white text-sm font-bold hover:bg-[#EA580C] disabled:opacity-60">
                {busy ? <><Loader2 size={15} className="animate-spin" /> {phaseLabel}</> : <><Wand2 size={15} /> Transcribe & auto-fill</>}
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

/* ---------- main studio ---------- */
export default function JumpstartStudio({ context = 'business' } = {}) {
  const [sessions, setSessions] = useState([]);
  const [framework, setFramework] = useState([]);
  const [active, setActive] = useState(null);
  const [activeId, setActiveId] = useState(null);
  const [loadingList, setLoadingList] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [tab, setTab] = useState('interview');
  const pollRef = useRef(null);
  const deliveredRef = useRef(false);
  const [building, setBuilding] = useState(false);
  const sitePollRef = useRef(null);
  const siteDoneRef = useRef(false);
  const [refining, setRefining] = useState(false);
  const [creditsLow, setCreditsLow] = useState(false);
  const [newInitial, setNewInitial] = useState({});

  // Launchpad: open a pre-filled New Session modal when the Clarity Hub sends us here.
  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    if (q.get('new') === '1') {
      setNewInitial({
        client_name: q.get('client_name') || '',
        client_email: q.get('client_email') || '',
        business_name: q.get('business_name') || '',
      });
      setShowNew(true);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);


  const handleAiError = useCallback((msg) => {
    if (msg && /credit|universal key|add balance/i.test(msg)) setCreditsLow(true);
    else if (msg) toast.error(msg);
  }, []);

  const loadSessions = useCallback(() => {
    ax.get(`${API}/coaching/sessions?context=${context}`).then((r) => setSessions(r.data || [])).catch(() => {}).finally(() => setLoadingList(false));
  }, [context]);

  useEffect(() => { loadSessions(); }, [loadSessions]);
  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); if (sitePollRef.current) clearInterval(sitePollRef.current); }, []);

  const openSession = async (id) => {
    setActiveId(id);
    setTab('interview');
    setBuilding(false);
    if (sitePollRef.current) { clearInterval(sitePollRef.current); sitePollRef.current = null; }
    try {
      const { data } = await ax.get(`${API}/coaching/sessions/${id}`);
      setActive(data);
      if (data.deliverable) setTab('blueprint');
      const fw = await ax.get(`${API}/coaching/framework?flow=${data.flow}`);
      setFramework(fw.data.stages || []);
    } catch { toast.error('Could not load session'); }
  };

  const saveAnswer = async (qid, val) => {
    if (!active) return;
    const prev = (active.answers || {})[qid] || '';
    if (prev === val) return;
    const next = { ...(active.answers || {}), [qid]: val };
    setActive((a) => ({ ...a, answers: next }));
    try { await ax.patch(`${API}/coaching/sessions/${active.session_id}`, { answers: { [qid]: val } }); }
    catch { toast.error('Save failed'); }
  };

  const startPolling = (id) => {
    if (pollRef.current) clearInterval(pollRef.current);
    deliveredRef.current = false;
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await ax.get(`${API}/coaching/sessions/${id}`);
        if (deliveredRef.current) return;
        if (data.status === 'completed' && data.deliverable) {
          deliveredRef.current = true;
          clearInterval(pollRef.current); pollRef.current = null;
          setActive(data); setGenerating(false); setTab('blueprint');
          loadSessions();
          toast.success('Blueprint ready');
        } else if (data.generation_error) {
          deliveredRef.current = true;
          clearInterval(pollRef.current); pollRef.current = null;
          setGenerating(false);
          handleAiError(data.generation_error);
        }
      } catch { /* keep polling */ }
    }, 4000);
  };

  const generate = async () => {
    if (!active) return;
    setGenerating(true);
    try {
      await ax.post(`${API}/coaching/sessions/${active.session_id}/generate`);
      startPolling(active.session_id);
    } catch (e) {
      setGenerating(false);
      handleAiError(e?.response?.data?.detail || 'Could not start generation');
    }
  };

  const emailDeliverable = async (email) => {
    await ax.post(`${API}/coaching/sessions/${active.session_id}/email`, { email });
  };

  const saveDeliverable = async (markdown) => {
    const { data } = await ax.put(`${API}/coaching/sessions/${active.session_id}/deliverable`, { markdown });
    setActive((a) => ({ ...a, deliverable: data.deliverable, status: 'completed' }));
  };

  const refineDeliverable = async (instruction) => {
    if (!active) return;
    setRefining(true);
    try {
      await ax.post(`${API}/coaching/sessions/${active.session_id}/refine`, { instruction });
      if (pollRef.current) clearInterval(pollRef.current);
      deliveredRef.current = false;
      pollRef.current = setInterval(async () => {
        try {
          const { data } = await ax.get(`${API}/coaching/sessions/${active.session_id}`);
          if (deliveredRef.current) return;
          if (data.status === 'completed') {
            deliveredRef.current = true;
            clearInterval(pollRef.current); pollRef.current = null;
            setActive(data); setRefining(false);
            if (data.generation_error) handleAiError(data.generation_error);
            else toast.success('Blueprint updated');
          }
        } catch { /* keep polling */ }
      }, 4000);
    } catch (e) {
      setRefining(false);
      handleAiError(e?.response?.data?.detail || 'Could not start AI edit');
    }
  };

  const buildWebsite = async () => {
    if (!active) return;
    setBuilding(true);
    try {
      await ax.post(`${API}/coaching/sessions/${active.session_id}/website`);
      if (sitePollRef.current) clearInterval(sitePollRef.current);
      siteDoneRef.current = false;
      sitePollRef.current = setInterval(async () => {
        try {
          const { data } = await ax.get(`${API}/coaching/sessions/${active.session_id}`);
          if (siteDoneRef.current) return;
          if (data.website_status === 'ready') {
            siteDoneRef.current = true;
            clearInterval(sitePollRef.current); sitePollRef.current = null;
            setActive(data); setBuilding(false);
            toast.success('Website ready');
          } else if (data.website_status === 'failed') {
            siteDoneRef.current = true;
            clearInterval(sitePollRef.current); sitePollRef.current = null;
            setBuilding(false);
            handleAiError(data.website_error || 'Website generation failed');
          }
        } catch { /* keep polling */ }
      }, 5000);
    } catch (e) {
      setBuilding(false);
      handleAiError(e?.response?.data?.detail || 'Could not start website build');
    }
  };

  const deleteSession = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm('Delete this session?')) return;
    try {
      await ax.delete(`${API}/coaching/sessions/${id}`);
      if (activeId === id) { setActive(null); setActiveId(null); }
      loadSessions();
    } catch { toast.error('Delete failed'); }
  };

  /* ---- credits-low banner (shared) ---- */
  const creditsBanner = creditsLow ? (
    <div data-testid="credits-low-banner" className="mb-4 rounded-xl border border-amber-300 bg-amber-50 p-4 flex items-start gap-3">
      <AlertTriangle size={18} className="text-amber-600 shrink-0 mt-0.5" />
      <div className="flex-1">
        <div className="font-bold text-amber-900 text-sm">AI credits ran out</div>
        <p className="text-sm text-amber-800">Top up to keep generating blueprints, websites, and auto-fills — go to <strong>Profile → Universal Key → Add Balance</strong>, then try again.</p>
      </div>
      <button onClick={() => setCreditsLow(false)} data-testid="dismiss-credits-banner" className="text-amber-700 hover:text-amber-900"><X size={16} /></button>
    </div>
  ) : null;

  /* ---- session detail view ---- */
  if (active) {
    return (
      <div data-testid="jumpstart-studio">
        {creditsBanner}
        <button onClick={() => { setActive(null); setActiveId(null); loadSessions(); }} data-testid="back-to-list-btn"
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#475569] hover:text-[#1D4ED8] mb-4">
          <ArrowLeft size={16} /> All sessions
        </button>
        <div className="flex flex-wrap items-start justify-between gap-3 mb-5">
          <div>
            <div className="text-xs font-bold uppercase tracking-wide text-[#F97316]">{FLOW_LABEL[active.flow]}</div>
            <h2 className="text-2xl font-bold text-[#0A0F1C]">{active.client_name}{active.business_name ? ` — ${active.business_name}` : ''}</h2>
          </div>
          {active.deliverable && (
            <div className="inline-flex rounded-lg border border-[#E2E8F0] overflow-hidden">
              <button onClick={() => setTab('interview')} data-testid="tab-interview" className={`px-4 py-2 text-sm font-semibold ${tab === 'interview' ? 'bg-[#1D4ED8] text-white' : 'text-[#475569]'}`}>Interview</button>
              <button onClick={() => setTab('blueprint')} data-testid="tab-blueprint" className={`px-4 py-2 text-sm font-semibold ${tab === 'blueprint' ? 'bg-[#1D4ED8] text-white' : 'text-[#475569]'}`}>Blueprint</button>
            </div>
          )}
        </div>

        {tab === 'blueprint' && active.deliverable ? (
          <Blueprint session={active} onEmail={emailDeliverable} onBuildWebsite={buildWebsite} building={building}
            onSaveDeliverable={saveDeliverable} onRefine={refineDeliverable} refining={refining}
            onAssigned={(data) => setActive((a) => ({ ...a, client_email: data.client_email, client_id: data.client_id, assigned_at: new Date().toISOString() }))} />
        ) : (
          <>
            <ImportPanel session={active} onImported={(data) => setActive(data)} onAiError={handleAiError} />
            <Interview session={active} framework={framework} onSaveAnswer={saveAnswer} key={active.updated_at} />
            <div className="sticky bottom-4 mt-6 flex justify-center">
              <button onClick={generate} disabled={generating} data-testid="generate-blueprint-btn"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-gradient-to-r from-[#1D4ED8] to-[#F97316] text-white font-bold shadow-lg hover:opacity-95 disabled:opacity-70">
                {generating ? <><Loader2 size={18} className="animate-spin" /> Building blueprint… (~1–2 min)</> : <><Wand2 size={18} /> {active.deliverable ? 'Regenerate' : 'Generate'} AI Blueprint</>}
              </button>
            </div>
          </>
        )}
      </div>
    );
  }

  /* ---- list view ---- */
  return (
    <div data-testid="jumpstart-studio">
      {creditsBanner}
      <div className="flex items-center justify-between mb-5">
        <div>
          <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-[#F97316] mb-1">
            <Sparkles size={14} /> Coaching Tool
          </div>
          <h2 className="text-2xl font-bold text-[#0A0F1C]">{context === 'nonprofit' ? 'Seven Stage Nonprofit Jumpstart Sessions' : '7 Stage Business Jumpstart Sessions'}</h2>
          <p className="text-sm text-[#64748B] mt-1 max-w-2xl">{context === 'nonprofit' ? 'Lead a nonprofit through the 7-stage discovery, then let AI build their full launch blueprint — mission, programs, board, funding and a launch plan you can push straight into the 90-day Accelerator.' : 'Lead a client through the 7-stage discovery, then let AI build their full strategic blueprint — mission, positioning, offers, launch plan and a master build prompt.'}</p>
        </div>
        <button onClick={() => setShowNew(true)} data-testid="new-session-btn"
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[#1D4ED8] text-white font-semibold text-sm hover:bg-[#2563EB] shrink-0">
          <Plus size={16} /> New Session
        </button>
      </div>

      {loadingList ? (
        <div className="flex justify-center py-16"><Loader2 size={26} className="animate-spin text-[#1D4ED8]" /></div>
      ) : sessions.length === 0 ? (
        <div className="bg-white border border-[#E2E8F0] rounded-xl p-12 text-center" data-testid="no-sessions">
          <FileText size={36} className="mx-auto mb-3 text-[#CBD5E1]" />
          <p className="text-[#475569] font-medium">No coaching sessions yet.</p>
          <p className="text-sm text-[#94A3B8] mt-1">{context === 'nonprofit' ? "Start a new session to begin a nonprofit's Seven Stage Jumpstart." : "Start a new session to begin a client's Business Jumpstart."}</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="sessions-grid">
          {sessions.map((s) => (
            <button key={s.session_id} onClick={() => openSession(s.session_id)} data-testid={`session-card-${s.session_id}`}
              className="text-left bg-white border border-[#E2E8F0] rounded-xl p-5 hover:shadow-lg hover:border-[#1D4ED8]/40 transition-all group">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-bold text-[#0A0F1C] truncate">{s.client_name}</div>
                  {s.business_name && <div className="text-xs text-[#64748B] truncate">{s.business_name}</div>}
                </div>
                <span onClick={(e) => deleteSession(s.session_id, e)} data-testid={`delete-session-${s.session_id}`}
                  className="p-1 text-[#CBD5E1] hover:text-red-500 shrink-0"><Trash2 size={15} /></span>
              </div>
              <div className="flex items-center gap-2 mt-3">
                <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-[#EFF6FF] text-[#1D4ED8]">{FLOW_LABEL[s.flow]}</span>
                {s.has_deliverable
                  ? <span className="inline-flex items-center gap-1 text-[10px] font-bold text-emerald-600"><CheckCircle2 size={12} /> Blueprint ready</span>
                  : <span className="text-[10px] text-[#94A3B8]">{s.answer_count} answers</span>}
              </div>
            </button>
          ))}
        </div>
      )}

      {showNew && <NewSessionModal context={context} initial={newInitial} onClose={() => setShowNew(false)} onCreated={(s) => { setShowNew(false); loadSessions(); openSession(s.session_id); }} />}
    </div>
  );
}
